import { ICampusSections } from '../components/unscheduled_sections_by_campus/unscheduled_sections_by_campus';
import { SectionTime } from '../../sections/builders/section_time_builder';
import { ScheduledSectionStatus } from '../../course_scheduler/types/scheduled_section_status_enum';
import {
  ISection,
  ICampus,
  IUnscheduledSectionsByCampusVisibility,
  ICampusSectionGroup,
  IAllSectionsDisplayedByCampus
} from '../../../interfaces/scheduler_interfaces';
import { UnscheduledSectionServant } from '../servants/unscheduled_section_servant';
import * as _4 from 'lodash4';
import { SectionSwapModel } from '../../../swap/models/course_section_swap_model';
import { ISectionsMeta } from '../../sections/datasources/sections_datasource';
import { SelectedSectionFiltersModel } from '../../../components/section_filters/models/selected_section_filters_model';
export class UnscheduledSectionFilters {
  constructor(
    private ngUnscheduledSectionServant: UnscheduledSectionServant,
    private ngCourseSectionSwapModel: SectionSwapModel,
    private ngSelectedSectionFiltersModel: SelectedSectionFiltersModel
  ) {}
  filterFullSections(unscheduledSections: ISection[]): ISection[] {
    return _4.filter(unscheduledSections, (unscheduledSection) =>
      this.ngUnscheduledSectionServant.isSectionFull(unscheduledSection)
    );
  }
  filterConflictingSections(unscheduledSections: ISection[]): ISection[] {
    return _4.filter(unscheduledSections, (unscheduledSection) =>
      this.ngUnscheduledSectionServant.isSectionConflicting(unscheduledSection)
    );
  }
  filterAvailableSections(unscheduledSections: ISection[]): ISection[] {
    return _4.filter(unscheduledSections, (unscheduledSection) =>
      this.ngUnscheduledSectionServant.isSectionAvailable(unscheduledSection)
    );
  }

  filterOnlineSections(unscheduledSections: ISection[]) {
    return _4.filter(unscheduledSections, (section) => this.ngUnscheduledSectionServant.isOnlineSection(section));
  }

  filterSectionsBySectionType(unscheduledSections: ISection[], sectionType: number) {
    return _4.filter(unscheduledSections, (section) => section.section_type === sectionType);
  }

  filterSectionsByCourse(unscheduledSections: ISection[], courseId: number) {
    return _4.filter(unscheduledSections, (section) => section.course === courseId);
  }

  filterRegisteredSections(unscheduledSections: ISection[]) {
    return _4.filter(unscheduledSections, (section) => section.status !== ScheduledSectionStatus.REGISTERED);
  }

  filterSwapSections(unscheduledSections: ISection[]) {
    return _4.filter(unscheduledSections, (section) => section.status === ScheduledSectionStatus.UNSCHEDULED);
  }

  sortSections(sortOrder: number, sections: ISection[]) {
    // Sort sections by
    // Conflict status - no conflict first.
    // length of duration - descending,
    // Start date - ascending,
    // day of the week- ascending,
    // time of day - ascending,
    // No of seats available - descending,
    // instructor name

    return _4.sortBy(sections, [
      (section: ISection) => this.ngUnscheduledSectionServant.isSectionConflicting(section),
      (section: ISection) => sortOrder * moment(section.class_end_dt).diff(moment(section.class_start_dt), 'hours'),
      (section: ISection) => moment(section.class_start_dt).unix(),
      (section: ISection) => this.earliestTimeOfSectionAsUnix(section),
      (section: ISection) => sortOrder * section._availability.avail_seats,
      (section: ISection) => section.instructor_name,
      (section: ISection | ISectionsMeta) => _4.get(section, '_fullLocationName')
    ]);
  }
  groupByCampuses(unscheduledSections: ISection[]) {
    // Separate online and non online sections
    // Group non online sections by campus
    // Mix online sections group with other campus groups.
    // Sort section in each group
    const onlineSections = this.filterOnlineSections(unscheduledSections);
    const nonOnlineSections = _4.filter(
      unscheduledSections,
      (section) => !this.ngUnscheduledSectionServant.isOnlineSection(section)
    );
    const REVERSE_DEFAULT_SORTING = -1;

    const sectionsByCampus: ICampusSections[] = _4.sortBy(
      _4.map(
        _4.groupBy(nonOnlineSections, (section: ISection) => section._campus.name),
        (group: ISection[], key: string) => ({
          name: key,
          sections: group,
          limitedSections: [],
          campus_rank: _4.get(_4.first(group), '_campus.rank')
        })
      ),
      ['campus_rank', 'name']
    );

    if (!_4.isEmpty(onlineSections)) {
      const sortedOnlineSections = this.sortSections(REVERSE_DEFAULT_SORTING, onlineSections);
      const onlineSectionsGroup = {
        name: 'Online Classes',
        sections: sortedOnlineSections,
        limitedSections: []
      };
      sectionsByCampus.push(onlineSectionsGroup);
    }

    _4.forEach(sectionsByCampus, (campusGroup) => this.sortSections(REVERSE_DEFAULT_SORTING, campusGroup.sections));

    return sectionsByCampus;
  }

  earliestTimeOfSectionAsUnix(section: ISection) {
    const endOfWeek = moment().endOf('week').unix();
    const sectionTimesSortedByDayOfWeekAndFromTime = _4.map(section._section_times, (time) =>
      time.day_of_week === 8 ? endOfWeek : moment(time.from_tm, 'HH:mm:ss').weekday(time.day_of_week).unix()
    );
    return _4.size(sectionTimesSortedByDayOfWeekAndFromTime) > 0
      ? _4.first(sectionTimesSortedByDayOfWeekAndFromTime)
      : endOfWeek;
  }

  limitByThreshold(campusGroup: ICampusSections[], visibilityModel: any): ICampusSectionGroup[] {
    const campusGroupLimitedByThreshold = _4.map(campusGroup, (campusGroupItem: ICampusSectionGroup) => {
      const matchingLimit: IUnscheduledSectionsByCampusVisibility = visibilityModel.get(campusGroupItem.name);
      campusGroupItem.limitedSections = _4.take(campusGroupItem.sections, matchingLimit.displayQuantity);
      return campusGroupItem;
    });
    return campusGroupLimitedByThreshold;
  }

  allSectionsDisplayedByCampus(
    nonLimitedSections: ICampusSectionGroup[],
    limitedSections: ICampusSectionGroup[]
  ): IAllSectionsDisplayedByCampus[] {
    const sectionsDisplayedByCampus = _4.map(limitedSections, (limitedSection: ICampusSectionGroup) => {
      const limitedSectionGroupName = limitedSection.name;
      const matchingFullSectionGroup = _4.find(nonLimitedSections, ['name', limitedSectionGroupName]);
      const areAllSectionsDisplayed = _4.isUndefined(matchingFullSectionGroup)
        ? false
        : _4.gte(limitedSection.sections, matchingFullSectionGroup.sections);

      return {
        campusName: limitedSectionGroupName,
        allSectionsDisplayed: areAllSectionsDisplayed
      };
    });

    return sectionsDisplayedByCampus;
  }

  _filterSectionLabels(sections: ISection[], selectedSectionLabels: any) {
    return _4.filter(sections, (section) => {
      const intersection = _4.intersectionBy(section._section_labels.section_label, selectedSectionLabels, 'value');
      return !_4.isEmpty(intersection);
    });
  }

  applySectionFilters(unscheduledSections: ISection[]) {
    if (_4.isEmpty(unscheduledSections)) {
      return unscheduledSections;
    }
    const selectedInstructors = _4.get(this.ngSelectedSectionFiltersModel.read(), 'instructors', []);
    const selectedSectionLabels = _4.get(this.ngSelectedSectionFiltersModel.read(), 'section_labels', {});
    const foundInstructors = _4.filter(
      unscheduledSections,
      (section: ISection) => _4.indexOf(selectedInstructors, section.instructor_name) > -1
    );
    if (!_4.isEmpty(selectedInstructors)) {
      unscheduledSections = foundInstructors;
    }
    if (!_4.isEmpty(selectedSectionLabels)) {
      unscheduledSections = this._filterSectionLabels(unscheduledSections, selectedSectionLabels);
    }
    return unscheduledSections;
  }
}

angular.module('capability-ng-scheduler').service('ngUnscheduledSectionFilters', UnscheduledSectionFilters);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/unscheduled_sections/filters/unscheduled_section_filters.ts